import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Input } from "./ui/input";
import { ArrowLeft, Calendar, Clock, Filter, Search, Tag, User, BookOpen } from "lucide-react";
import { useMemo, useState } from "react";
import { BlogPost } from "./BlogPost";
import { NewsletterForm } from "./NewsletterForm";
import { getAllBlogPosts, getBlogCategories, getBlogTags, getRelatedBlogPosts, type BlogPostData } from "@/lib/blog-loader";

interface BlogProps {
  onBackToLanding: () => void;
}

type BlogView = 'list' | 'post';

export function Blog({ onBackToLanding }: BlogProps) {
  // STATE MANAGEMENT
  const [currentView, setCurrentView] = useState<BlogView>('list');
  const [selectedPost, setSelectedPost] = useState<BlogPostData | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [selectedTag, setSelectedTag] = useState<string | null>(null);

  // Load dynamic data
  const allPosts = getAllBlogPosts();
  const categories = ["All", ...getBlogCategories()];
  const tags = getBlogTags();

  // NAVIGATION FUNCTIONS
  const handlePostClick = (post: BlogPostData) => {
    setSelectedPost(post);
    setCurrentView('post');
  };

  const handleBackToBlog = () => {
    setCurrentView('list');
    setSelectedPost(null);
  };

  // FILTERED POSTS
  const filteredPosts = useMemo(() => {
    return allPosts.filter(post => {
      if (!post.published) return false;
      
      // Search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const matchesSearch = 
          post.title.toLowerCase().includes(query) ||
          post.excerpt.toLowerCase().includes(query) ||
          post.tags.some(tag => tag.toLowerCase().includes(query)) ||
          post.author.toLowerCase().includes(query);
        
        if (!matchesSearch) return false;
      }
      
      // Category filter
      if (selectedCategory !== "All" && post.category !== selectedCategory) {
        return false;
      }
      
      // Tag filter
      if (selectedTag && !post.tags.includes(selectedTag)) {
        return false;
      }
      
      return true;
    });
  }, [allPosts, searchQuery, selectedCategory, selectedTag]);

  // UTILITY FUNCTIONS
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const clearFilters = () => {
    setSearchQuery("");
    setSelectedCategory("All");
    setSelectedTag(null);
  };

  // RENDER INDIVIDUAL POST VIEW
  if (currentView === 'post' && selectedPost) {
    const relatedPosts = getRelatedBlogPosts(selectedPost);
    return (
      <BlogPost 
        post={selectedPost}
        onBackToBlog={handleBackToBlog}
        relatedPosts={relatedPosts}
      />
    );
  }

  // RENDER BLOG LIST VIEW
  return (  
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b bg-card">
        <div className="container py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={onBackToLanding}
                className="flex items-center space-x-2"
              >
                <ArrowLeft className="h-4 w-4" />
                <span>Back to Home</span>
              </Button>
              <div className="h-6 w-px bg-border" />
              <div>
                <h1 className="text-2xl font-semibold">Decision Making Blog</h1>
                <p className="text-sm text-muted-foreground">
                  Insights, strategies, and research on better decision making
                </p>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container py-12">
        
        {/* Blog Introduction */}
        <div className="text-center mb-12">
          <h2 className="text-4xl font-medium mb-4">
            Master the Art of Decision Making
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Explore evidence-based strategies, psychological insights, and practical frameworks 
            to improve your decision-making skills and achieve better outcomes in life and work.
          </p>
        </div>

        {/* Search and Filters */}
        <div className="mb-8">
          <div className="flex flex-col md:flex-row gap-4 mb-6">
            {/* Search Input */}
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search articles, authors, or topics..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            
            {/* Clear Filters Button */}
            {(searchQuery || selectedCategory !== "All" || selectedTag) && (
              <Button variant="outline" onClick={clearFilters}>
                <Filter className="h-4 w-4 mr-2" />
                Clear Filters
              </Button>
            )}
          </div>

          {/* Category Filter */}
          <div className="mb-4">
            <h3 className="text-sm font-medium mb-2">Categories</h3>
            <div className="flex flex-wrap gap-2">
              {categories.map(category => (
                <Button
                  key={category}
                  variant={selectedCategory === category ? "default" : "outline"}
                  size="sm"
                  onClick={() => setSelectedCategory(category)}
                >
                  {category}
                </Button>
              ))}
            </div>
          </div>

          {/* Popular Tags */}
          <div className="mb-6">
            <h3 className="text-sm font-medium mb-2">Popular Tags</h3>
            <div className="flex flex-wrap gap-2">
              {tags.slice(0, 8).map(tag => (
                <Badge
                  key={tag}
                  variant={selectedTag === tag ? "default" : "secondary"}
                  className="cursor-pointer hover:bg-primary hover:text-primary-foreground"
                  onClick={() => setSelectedTag(selectedTag === tag ? null : tag)}
                >
                  <Tag className="h-3 w-3 mr-1" />
                  {tag}
                </Badge>
              ))}
            </div>
          </div>

          {/* Results Count */}
          <div className="text-sm text-muted-foreground">
            {filteredPosts.length} article{filteredPosts.length !== 1 ? 's' : ''} found
            {searchQuery && ` for "${searchQuery}"`}
            {selectedCategory !== "All" && ` in ${selectedCategory}`}
            {selectedTag && ` tagged with "${selectedTag}"`}
          </div>
        </div>

        {/* Blog Posts Grid */}
        {filteredPosts.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredPosts.map((post) => (
              <Card 
                key={post.id} 
                className="group hover:shadow-lg transition-shadow cursor-pointer"
                onClick={() => handlePostClick(post)}
              >
                {/* Featured Image */}
                <div className="aspect-video relative overflow-hidden rounded-t-lg">
                  <img 
                    src={post.featuredImage} 
                    alt={post.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute top-4 left-4">
                    <Badge variant="secondary" className="bg-white/90 text-gray-800">
                      {post.category}
                    </Badge>
                  </div>
                </div>

                <CardHeader>
                  <CardTitle className="group-hover:text-primary transition-colors line-clamp-2">
                    {post.title}
                  </CardTitle>
                  <CardDescription className="line-clamp-3">
                    {post.excerpt}
                  </CardDescription>
                </CardHeader>

                <CardContent>
                  {/* Meta Information */}
                  <div className="flex items-center justify-between text-sm text-muted-foreground mb-4">
                    <div className="flex items-center space-x-4">
                      <div className="flex items-center space-x-1">
                        <Calendar className="h-3 w-3" />
                        <span>{formatDate(post.date)}</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <Clock className="h-3 w-3" />
                        <span>{post.readTime}</span>
                      </div>
                    </div>
                  </div>

                  {/* Author */}
                  <div className="flex items-center space-x-2 mb-4">
                    <User className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm">{post.author}</span>
                  </div>

                  {/* Tags */}
                  <div className="flex flex-wrap gap-1 mb-4">
                    {post.tags.slice(0, 3).map(tag => (
                      <Badge 
                        key={tag} 
                        variant="outline" 
                        className="text-xs cursor-pointer hover:bg-primary hover:text-primary-foreground"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedTag(tag);
                        }}
                      >
                        {tag}
                      </Badge>
                    ))}
                    {post.tags.length > 3 && (
                      <Badge variant="outline" className="text-xs">
                        +{post.tags.length - 3} more
                      </Badge>
                    )}
                  </div>

                  {/* Read More Button */}
                  <Button 
                    variant="outline" 
                    className="w-full group-hover:bg-primary group-hover:text-primary-foreground transition-colors"
                    onClick={(e) => {
                      e.stopPropagation();
                      handlePostClick(post);
                    }}
                  >
                    <BookOpen className="h-4 w-4 mr-2" />
                    Read Article
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          /* No Results */
          <div className="text-center py-12">
            <BookOpen className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-medium mb-2">No articles found</h3>
            <p className="text-muted-foreground mb-4">
              Try adjusting your search or filter criteria
            </p>
            <Button variant="outline" onClick={clearFilters}>
              Clear all filters
            </Button>
          </div>
        )}

        {/* Newsletter Signup */}
        <div className="mt-16 text-center">
          <NewsletterForm />
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t mt-16">
        <div className="container py-8">
          <div className="text-center text-sm text-muted-foreground">
            <p>© 2025 Decisionr.xyz. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}